import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { useModules } from "@/hooks/useCatalog";
import { ArrowRight, Boxes, Activity, Gauge, Truck, QrCode, ClipboardCheck, ListChecks, Wrench, Bell, Zap, LineChart, Hammer } from "lucide-react";
import { track } from "@/lib/analytics";

const icons: Record<string, typeof Boxes> = {
  mes: Boxes,
  "machine-monitoring": Activity,
  "oee-downtime": Gauge,
  "material-flow": Truck,
  "qr-traceability": QrCode,
  "quality-management": ClipboardCheck,
  "job-orders": ListChecks,
  "cmms-maintenance": Wrench,
  andon: Bell,
  "energy-management": Zap,
  "dashboards-ai": LineChart,
  "tools-management": Hammer,
};

export default function ModulesGrid({ compact = false }: { compact?: boolean }) {
  const { t } = useLanguage();
  const { modules } = useModules();
  return (
    <section id="modules" className="py-16 md:py-20 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="eyebrow mb-3">{t("Modules")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("One modular platform. Start with what you need. Expand when you are ready.")}</h2>
          <p className="text-muted-foreground">
            {t("BEEDIGIT modules work independently or together as a full manufacturing execution platform.")}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => {
            const Icon = icons[m.slug] || Boxes;
            return (
              <Link
                key={m.slug}
                to={`/modules/${m.slug}`}
                onClick={() => track("module_card_click", { slug: m.slug })}
                className="group rounded-lg border border-border bg-card p-5 hover-glow flex flex-col"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-9 h-9 rounded-md bg-primary/15 text-primary inline-flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-semibold group-hover:text-primary">{t(m.title.split(" — ")[0])}</h3>
                </div>
                <p className="text-sm text-foreground/85 mb-2">{t(m.short)}</p>
                {!compact && <p className="text-xs text-muted-foreground mb-3">{t(m.description)}</p>}
                <span className="mt-auto text-xs text-primary inline-flex items-center gap-1">
                  {t("Open module")} <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}